import { ButtonType, ButtonWidget } from '@tableau/widgets';
import * as React from 'react';

import { L10n } from '~commonInfra/L10n/L10n';
import { Messages } from '~src/codegen/Localize';
import { ListDetailLayout } from '~src/commonInfra/components/ListDetailLayout';
import { ExplorerStyles } from '~src/spacesPlatform/components/Explorer/ExplorerStyles';
import { SpaceContainerStyles } from '~src/spacesPlatform/style/SpaceContainerStyles';

import { IAdminProfile, IAdminProfileDetails } from '../models/IAdminProfile';
import { AdminProfileStyles } from '../styles/AdminProfileStyles';
import { AdminProfileDetails } from './AdminProfileDetails';
import { AdminProfileList } from './AdminProfileList';

export interface AdminProfilesSpaceProps {
  profiles: ReadonlyArray<IAdminProfile>;

  /** Loading state for the profiles list */
  isLoadingProfiles?: boolean;

  selectedProfileId: string | null;
  selectedProfileDetails: IAdminProfileDetails | null;
  isLoadingDetails: boolean;
  activeSubspace: 'permissions' | 'sites' | 'users';

  onAddProfile?: () => void;
  onEditProfile?: (profile: IAdminProfile) => void;
  onDeleteProfile?: (profile: IAdminProfile) => void;
  onSelectProfile: (profile: IAdminProfile) => void;
  onProfileSitesUpdated: () => void;
  urlForSubspace: (subspace?: 'permissions' | 'sites' | 'users') => string;
}

export const AdminProfilesSpace: React.FC<AdminProfilesSpaceProps> = ({
  profiles,
  isLoadingProfiles = false,
  selectedProfileId,
  selectedProfileDetails,
  isLoadingDetails,
  activeSubspace,
  onAddProfile,
  onEditProfile,
  onDeleteProfile,
  onSelectProfile,
  onProfileSitesUpdated,
  urlForSubspace,
}) => {
  const renderList = React.useCallback(() => (
    <AdminProfileList
      profiles={profiles}
      isLoading={isLoadingProfiles}
      selectedProfileId={selectedProfileId}
      onSelectProfile={onSelectProfile}
      onEditProfile={onEditProfile}
      onDeleteProfile={onDeleteProfile}
    />
  ), [profiles, isLoadingProfiles, selectedProfileId, onSelectProfile, onEditProfile, onDeleteProfile]);

  const renderDetail = React.useCallback(() => (
    <AdminProfileDetails
      profile={selectedProfileDetails}
      isLoading={isLoadingDetails}
      activeSubspace={activeSubspace}
      onProfileSitesUpdated={onProfileSitesUpdated}
      urlForSubspace={urlForSubspace}
    />
  ), [selectedProfileDetails, isLoadingDetails, activeSubspace, onProfileSitesUpdated, urlForSubspace]);

  return (
    <div className={ SpaceContainerStyles.SpaceContainer }>
      <div className={SpaceContainerStyles.MastheadArea}>
        <div className={ExplorerStyles.SpaceHeaderContent}>
          <div className={ExplorerStyles.SpaceHeaderText}>
            {Messages.adminProfiles()}
          </div>
          <div className={AdminProfileStyles.SpaceHeaderCount} data-tb-test-id={'admin-profiles-header-count'}>
            {L10n.localizedCount(profiles.length)}
          </div>
        </div>
        <div className={AdminProfileStyles.SpaceHeaderDescription}>
          {Messages.adminProfilesDescription()}
        </div>
      </div>
      <div className={AdminProfileStyles.SpaceContentWrapper}>
        <ListDetailLayout
          renderList={renderList}
          renderDetail={renderDetail}
          toolbarProps={{
            toolbarVisibility: 'enabled',
            toolbarContentSpecificControls: {
              beginning: newAdminProfileButton(onAddProfile),
            },
          }}
          collapsible={false}
          testId="admin-profiles-layout"
        />
      </div>
    </div>
  );
};

export function newAdminProfileButton(onAddProfile?: () => void): JSX.Element {
  if (onAddProfile) {
    return (
      <ButtonWidget
        children={Messages.adminProfilesNewButton()}
        buttonType={ButtonType.Outline}
        handleClick={onAddProfile}
        tb-test-id={'action-create-new-admin-profile'}
        testId={'action-create-new-admin-profile'}/>
    );
  }
  return null;
}

AdminProfilesSpace.displayName = 'AdminProfilesSpace';
